import { Link } from "@tanstack/react-router"
import { CloudOff, KeyRound, Lock, RotateCw, TriangleAlert } from "lucide-react"
import type { ReactNode } from "react"
import { CellUnreachable, HttpFailure, refusalOfError } from "@/lib/api"
import { useT } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { Button } from "./ui/button"
import { Skeleton } from "./ui/skeleton"

function StateFrame({
  icon,
  title,
  children,
  actions,
  tone = "neutral",
  className,
}: {
  icon: ReactNode
  title: string
  children?: ReactNode
  actions?: ReactNode
  tone?: "neutral" | "danger" | "warning"
  className?: string
}) {
  return (
    <div
      role={tone === "neutral" ? "status" : "alert"}
      className={cn("flex max-w-lg flex-col items-start gap-3 px-6 py-14", className)}
    >
      <div
        className={cn(
          "flex size-8 items-center justify-center rounded-md border [&_svg]:size-4",
          tone === "danger" && "border-destructive/30 bg-destructive-soft text-destructive",
          tone === "warning" && "border-warning/30 bg-warning-soft text-warning",
          tone === "neutral" && "border-border bg-raised text-muted-foreground",
        )}
      >
        {icon}
      </div>
      <div className="space-y-1.5">
        <h2 className="text-base font-medium text-foreground">{title}</h2>
        {children ? (
          <div className="space-y-2 text-sm text-muted-foreground [&_code]:font-mono [&_code]:text-xs [&_code]:text-foreground/85">
            {children}
          </div>
        ) : null}
      </div>
      {actions ? <div className="mt-1 flex items-center gap-2">{actions}</div> : null}
    </div>
  )
}

/** An empty result that says why it is empty and how things get here. */
export function EmptyState({
  icon,
  title,
  children,
  actions,
}: {
  icon: ReactNode
  title: string
  children?: ReactNode
  actions?: ReactNode
}) {
  return (
    <StateFrame icon={icon} title={title} actions={actions}>
      {children}
    </StateFrame>
  )
}

/** Skeleton rows shaped like the table that will replace them. */
export function TableSkeleton({ rows = 8, columns = 4 }: { rows?: number; columns?: number }) {
  const t = useT()
  return (
    <div role="status" aria-busy="true" aria-label={t("states.loading")} className="divide-y divide-border-subtle">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex h-9 items-center gap-6 px-4">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton
              key={c}
              className={cn("h-3", c === 0 ? "w-56" : c === columns - 1 ? "ms-auto w-14" : "w-24")}
              style={{ opacity: 1 - r * 0.08 }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Any failed query: a refusal keeps its kernel category and explanation;
 * transport failures say what to check. Never a bare "something went wrong".
 */
export function ErrorState({
  error,
  onRetry,
  what,
  inline = false,
}: {
  error: unknown
  onRetry?: () => void
  what: string
  inline?: boolean
}) {
  const t = useT()
  const frame = inline ? "mx-0 px-0 py-8" : undefined
  const retry = onRetry ? (
    <Button size="sm" onClick={onRetry}>
      <RotateCw /> {t("connect.tryAgain")}
    </Button>
  ) : null
  const refused = refusalOfError(error)
  if (refused) {
    const { status, refusal } = refused
    if (status === 401) {
      return (
        <StateFrame
          className={frame}
          icon={<KeyRound />}
          tone="warning"
          title={t("states.credentialsRefused")}
          actions={
            <Button size="sm" variant="primary" asChild>
              <Link to="/connect">{t("states.updateConnection")}</Link>
            </Button>
          }
        >
          <p>{refusal.explanation}</p>
          <p>
            <code>{refusal.code}</code> · {refusal.category} · HTTP {status}
          </p>
        </StateFrame>
      )
    }
    if (refusal.category === "permission" || refusal.category === "visibility") {
      return (
        <StateFrame className={frame} icon={<Lock />} tone="warning" title={t("states.cannotRead", { what })}>
          <p>{refusal.explanation}</p>
          <p>
            <code>{refusal.code}</code> · {refusal.category} · HTTP {status}
          </p>
        </StateFrame>
      )
    }
    return (
      <StateFrame className={frame} icon={<TriangleAlert />} tone="danger" title={refusal.message} actions={retry}>
        <p>{refusal.explanation}</p>
        <p>
          <code>{refusal.code}</code> · {refusal.category} · HTTP {status}
        </p>
      </StateFrame>
    )
  }
  if (error instanceof CellUnreachable) {
    return (
      <StateFrame
        className={frame}
        icon={<CloudOff />}
        tone="danger"
        title={t("states.cannotReach", { url: error.baseUrl })}
        actions={retry}
      >
        <p>{t("states.unreachableHelp")}</p>
      </StateFrame>
    )
  }
  const status = error instanceof HttpFailure ? `HTTP ${error.status}` : null
  return (
    <StateFrame
      className={frame}
      icon={<TriangleAlert />}
      tone="danger"
      title={t("states.cannotLoad", { what })}
      actions={retry}
    >
      <p>{error instanceof Error ? error.message : String(error)}</p>
      {status ? <p>{status}</p> : null}
    </StateFrame>
  )
}

export { UnavailableBlock } from "@norn/widget-renderer/catalog"
